// src/pages/MembershipTiers.js
// Standalone admin page for managing membership tiers and settings.
// Extracted from CourtRentals.js — reads rentalTimeBlocks (defined there) to
// render the prime-blocks picker, but otherwise owns its own state and data.

import React, { useEffect, useState } from 'react';
import {
  collection, getDocs, getDoc, doc, setDoc, addDoc, updateDoc, deleteDoc, serverTimestamp,
  query, where, writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import Layout from '../components/Layout';
import { useBranding } from '../contexts/BrandingContext';
import {
  Box, Typography, Paper, Grid, TextField, MenuItem, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Divider,
  FormControlLabel, Switch, InputAdornment, Alert, CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import { logError } from '../utils/errorLogger';

// Normalize any text into a URL slug; used for the shareable /m/<slug> links.
const slugify = (v) => (v || '').trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');

const BILLING_MODELS = [
  { value: 'upfront',       label: 'Upfront — charge at booking' },
  { value: 'weekly_batch',  label: 'Weekly batch — charge once per week' },
  { value: 'monthly_batch', label: 'Monthly batch — charge once per month' },
];

const emptyTier = {
  name: '', slug: '', pricingMode: 'recurring', isDayPass: false, monthlyPrice: '', stripePriceId: '',
  weeklyRegularHours: '', weeklyPrimeHours: '', weeklyWeekendHours: '',
  overageRegularRate: '', overagePrimeRate: '', overageWeekendRate: '',
  dailyRegularHours: '', dailyPrimeHours: '', dailyWeekendHours: '',
  primeBlocked: false, courtReservationsBlocked: false, streamingIncluded: false,
  maxCourtsPerDay: '', maxAdvanceDays: '',
  maxBookingDurationHours: '',   // blank = the club's Reservation Guardrails
  overageBillingEnabled: null,   // null = inherit the club-wide setting
  maxHoursPerWeek: '', maxCourtsPerRequest: '',
  guestsPerMonth: '', guestsPerBooking: '',
  enabled: true, adminOnly: false, sortOrder: 0,
  // Public page display fields (edit-only; default to '' so AdultMembership falls back to hardcoded defaults)
  tagline: '', ctaLabel: '', priceSuffix: '', priceFootnote: '',
  bullets: '',         // textarea — one bullet per line; converted to array on save
  emphasize: false,    // "featured" tier (visual emphasis on public page)
  accentColor: '',     // hex color string for the colored dot/accent (e.g. "#C9A84C")
};

const defaultTierSettings = {
  billingModel: 'weekly_batch',
  // Days after an invoice before unpaid court time holds future bookings.
  // 0 = immediately, which is how it behaved before this was settable.
  paymentGraceDays: 0,
  primeBlockIds: [],
  requiredWaiverId: '', // id of a clubDocuments doc to gate membership purchases
  // Phase controls — read live by Schedule.js. Both OFF = observation mode:
  // policies are evaluated and recorded on bookings, nothing blocks or bills.
  policyEnforcementEnabled: false,
  overageBillingEnabled: false,
  // Go-live gate for member court booking. Blank = open. Enforced server-side
  // in createMemberBooking; Schedule.js mirrors it to hide the booking UI.
  reservationsOpenDate: '',
};

export default function MembershipTiers() {
  const branding = useBranding();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [tiers, setTiers] = useState([]);
  const [tierSettings, setTierSettings] = useState(defaultTierSettings);
  // Held locally and committed on blur: a date input reports partial values
  // mid-edit, and saving one of those would briefly reopen booking.
  const [openDateInput, setOpenDateInput] = useState('');
  const [timeBlocks, setTimeBlocks] = useState([]); // read-only here; managed in CourtRentals

  const [editingTierId, setEditingTierId] = useState(null);
  const [tierForm, setTierForm] = useState(emptyTier);
  const [tierDialog, setTierDialog] = useState(false);
  const [copyFromId, setCopyFromId] = useState('');
  const [copyNote, setCopyNote] = useState('');
  // Documents eligible to gate membership purchases (enabled + acceptance-style action).
  const [waiverDocs, setWaiverDocs] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [tierSnap, settingsDoc, tbSnap, docsSnap] = await Promise.all([
          getDocs(collection(db, 'membershipTiers')),
          getDoc(doc(db, 'settings', 'membershipTiers')),
          getDocs(collection(db, 'rentalTimeBlocks')),
          getDocs(collection(db, 'clubDocuments')),
        ]);
        setTiers(
          tierSnap.docs.map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        );
        if (settingsDoc.exists()) {
          const s = { ...defaultTierSettings, ...settingsDoc.data() };
          setTierSettings(s);
          setOpenDateInput(s.reservationsOpenDate || '');
        }
        setTimeBlocks(tbSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        // Only enabled docs whose action type is acceptance-style can serve as
        // a waiver gate. View-only / accept-or-reject don't qualify.
        setWaiverDocs(
          docsSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(d => d.enabled !== false && (d.actionType === 'accept' || d.actionType === 'signature'))
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        );
      } catch (e) {
        logError('MembershipTiers', e.message, e.stack);
        setError('Failed to load data.');
      }
      setLoading(false);
    })();
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────


  // Fields that describe how a plan BEHAVES. Deliberately excludes identity and
  // billing (name, slug, price, Stripe price id, pricingMode, isDayPass) and the
  // public marketing copy — copying those between plans would either duplicate a
  // listing or, worse, point two tiers at the same Stripe price.
  const COPYABLE_TIER_FIELDS = [
    'weeklyRegularHours', 'weeklyPrimeHours', 'weeklyWeekendHours',
    'overageRegularRate', 'overagePrimeRate', 'overageWeekendRate',
    'dailyRegularHours', 'dailyPrimeHours', 'dailyWeekendHours',
    'primeBlocked', 'courtReservationsBlocked', 'streamingIncluded',
    'maxCourtsPerDay', 'maxAdvanceDays', 'maxHoursPerWeek', 'maxCourtsPerRequest',
    'maxBookingDurationHours',
    'guestsPerMonth', 'guestsPerBooking',
  ];

  const copySettingsFrom = (sourceId) => {
    setCopyFromId(sourceId);
    if (!sourceId) { setCopyNote(''); return; }
    const src = tiers.find(t => t.id === sourceId);
    if (!src) return;
    const patch = {};
    for (const k of COPYABLE_TIER_FIELDS) {
      const v = src[k];
      // Booleans stay booleans; numbers become strings because the form's
      // number inputs are controlled with string values.
      patch[k] = typeof v === 'boolean' ? v : (v == null || v === '' ? '' : String(v));
    }
    setTierForm(f => ({ ...f, ...patch }));
    setCopyNote(`Copied hours, caps, limits and overage rates from “${src.name || sourceId}”. `
      + `Name, price, Stripe price ID and public page text were left unchanged — review and Save.`);
  };

  const openNewTier = () => {
    setEditingTierId(null);
    setTierForm({ ...emptyTier, sortOrder: tiers.length });
    setCopyFromId(''); setCopyNote('');
    setTierDialog(true);
  };

  const openEditTier = (t) => {
    setEditingTierId(t.id);
    setCopyFromId(''); setCopyNote('');
    setTierForm({
      name: t.name || '',
      slug: t.slug || '',
      pricingMode: t.pricingMode || 'recurring',
      isDayPass: !!t.isDayPass,
      monthlyPrice: String(t.monthlyPrice ?? ''),
      stripePriceId: t.stripePriceId || '',
      weeklyRegularHours: String(t.weeklyRegularHours ?? ''),
      weeklyPrimeHours: String(t.weeklyPrimeHours ?? ''),
      weeklyWeekendHours: String(t.weeklyWeekendHours ?? ''),
      overageRegularRate: String(t.overageRegularRate ?? ''),
      overagePrimeRate: String(t.overagePrimeRate ?? ''),
      overageWeekendRate: String(t.overageWeekendRate ?? ''),
      dailyRegularHours: t.dailyRegularHours == null ? '' : String(t.dailyRegularHours),
      dailyPrimeHours: t.dailyPrimeHours == null ? '' : String(t.dailyPrimeHours),
      dailyWeekendHours: t.dailyWeekendHours == null ? '' : String(t.dailyWeekendHours),
      primeBlocked: !!t.primeBlocked,
      courtReservationsBlocked: !!t.courtReservationsBlocked,
      streamingIncluded: !!t.streamingIncluded,
      // undefined stays null so the tier keeps inheriting rather than silently pinning
      overageBillingEnabled: t.overageBillingEnabled == null ? null : !!t.overageBillingEnabled,
      maxCourtsPerDay: t.maxCourtsPerDay == null ? '' : String(t.maxCourtsPerDay),
      maxAdvanceDays: t.maxAdvanceDays == null ? '' : String(t.maxAdvanceDays),
      maxBookingDurationHours: t.maxBookingDurationHours == null ? '' : String(t.maxBookingDurationHours),
      maxHoursPerWeek: t.maxHoursPerWeek == null ? '' : String(t.maxHoursPerWeek),
      maxCourtsPerRequest: t.maxCourtsPerRequest == null ? '' : String(t.maxCourtsPerRequest),
      guestsPerMonth: t.guestsPerMonth == null ? '' : String(t.guestsPerMonth),
      guestsPerBooking: t.guestsPerBooking == null ? '' : String(t.guestsPerBooking),
      enabled: t.enabled !== false,
      adminOnly: !!t.adminOnly,
      sortOrder: t.sortOrder ?? 0,
      // Display fields
      tagline: t.tagline || '',
      ctaLabel: t.ctaLabel || '',
      priceSuffix: t.priceSuffix || '',
      priceFootnote: t.priceFootnote || '',
      bullets: Array.isArray(t.bullets) ? t.bullets.join('\n') : (t.bullets || ''),
      emphasize: !!t.emphasize,
      accentColor: t.accentColor || '',
    });
    setTierDialog(true);
  };

  // Bring the members already on a tier back in line with what it now says.
  //
  // assignTier (UserProfile.js) copies a handful of tier fields onto
  // users.membership at assign time, and until now nothing ever rewrote them.
  // Every edit to a tier therefore left its members quoting whatever that tier
  // said on the day they were assigned — silently, and for as long as they held
  // the membership.
  //
  // This is a display problem, and it is worth being exact about that rather
  // than implying more: every path that enforces or prices anything reads the
  // tier doc, not this copy (createMemberBooking in functions/index.js,
  // allocationFor in src/utils/memberHours.js), and no server code reads the
  // copy at all. What it feeds is the three admin screens that quote a
  // member's weekly allowance — UserProfile, People, AthleteProfile — which is
  // precisely where a wrong number gets read out to a member and believed.
  //
  // Runs on every edit rather than only when the hours changed, because that
  // also repairs drift left behind by edits made before this existed. The cost
  // is a few dozen writes on a tier save, which is nothing next to an admin
  // reading a stale allowance off a screen.
  //
  // pricingMode is deliberately NOT synced, though assignTier writes it too.
  // Schedule.js reads a one_time membership with no expiresAt as expired, so
  // pushing a recurring -> one_time change down onto existing members would
  // lock every one of them out of booking in the same breath. That conversion
  // needs a per-member access-through date, which is a decision, not a sync.
  const syncMemberSnapshots = async (tierId, tier) => {
    const snap = await getDocs(query(
      collection(db, 'users'), where('membership.tierId', '==', tierId)));
    if (snap.empty) return 0;
    // Chunked because a Firestore batch caps at 500 writes. The largest tier
    // today holds 40-odd members, but a batch that overflows in a year's time
    // is a poor thing to find out about from a member.
    for (let i = 0; i < snap.docs.length; i += 400) {
      const batch = writeBatch(db);
      snap.docs.slice(i, i + 400).forEach(d => batch.update(d.ref, {
        'membership.tierName': tier.name,
        'membership.weeklyRegularHours': Number(tier.weeklyRegularHours) || 0,
        'membership.weeklyPrimeHours': Number(tier.weeklyPrimeHours) || 0,
      }));
      await batch.commit();
    }
    return snap.docs.length;
  };

  // A court-rental tier has no included hours, so its "overage" rates are
  // simply its hourly rates — see the rate labels below.
  const isCourtRentalTier = tierForm.pricingMode === 'court_rental';

  const saveTier = async () => {
    if (!tierForm.name.trim()) { setError('Tier name is required.'); return; }
    // Stripe Price ID is required for public recurring tiers; admin-only tiers can be free
    // and assigned manually, so they skip the Stripe check.
    if (tierForm.pricingMode === 'recurring' && !tierForm.adminOnly && !tierForm.stripePriceId.trim()) {
      setError('Stripe Price ID is required for recurring tiers (unless Admin-Only).'); return;
    }
    // calcOverage3 treats a bucket as "unconfigured" when BOTH the allowance and
    // the rate are zero, and unconfigured means unlimited at no charge. A tier
    // saved that way silently becomes free unlimited court time, so refuse it
    // unless the tier cannot reserve courts at all.
    if (tierForm.pricingMode === 'court_rental') {
      const anyRate = (parseFloat(tierForm.overageRegularRate) || 0)
        || (parseFloat(tierForm.overagePrimeRate) || 0)
        || (parseFloat(tierForm.overageWeekendRate) || 0);
      if (!anyRate) {
        setError('A court-rental tier charges entirely through its hourly rate — set at least one overage rate, or it grants free court time.');
        setSaving(false); return;
      }
    }
    if (!tierForm.courtReservationsBlocked) {
      const n = (v) => parseFloat(v) || 0;
      const noHours = !n(tierForm.weeklyRegularHours) && !n(tierForm.weeklyPrimeHours) && !n(tierForm.weeklyWeekendHours);
      const noRates = !n(tierForm.overageRegularRate) && !n(tierForm.overagePrimeRate) && !n(tierForm.overageWeekendRate);
      if (noHours && noRates) {
        setError('This tier includes no hours and has no overage rate, which grants unlimited free court time. '
          + 'Set an overage rate, include weekly hours, or turn on "Cannot reserve courts".');
        setSaving(false); return;
      }
    }
    setSaving(true); setError('');
    try {
      // Helper: blank/whitespace → null (use default); otherwise parsed number (including 0).
      const numOrNull = (raw, parser = parseFloat) => {
        const s = String(raw ?? '').trim();
        if (s === '') return null;
        const n = parser(s);
        return Number.isFinite(n) ? n : null;
      };
      const data = {
        name: tierForm.name.trim(),
        slug: slugify(tierForm.slug) || slugify(tierForm.name),
        pricingMode: tierForm.pricingMode || 'recurring',
        isDayPass: !!tierForm.isDayPass && tierForm.pricingMode === 'one_time',
        monthlyPrice: parseFloat(tierForm.monthlyPrice) || 0,
        stripePriceId: tierForm.stripePriceId.trim(),
        weeklyRegularHours: parseFloat(tierForm.weeklyRegularHours) || 0,
        weeklyPrimeHours: parseFloat(tierForm.weeklyPrimeHours) || 0,
        weeklyWeekendHours: parseFloat(tierForm.weeklyWeekendHours) || 0,
        overageRegularRate: parseFloat(tierForm.overageRegularRate) || 0,
        overagePrimeRate: parseFloat(tierForm.overagePrimeRate) || 0,
        overageWeekendRate: parseFloat(tierForm.overageWeekendRate) || 0,
        // Daily caps + booking policy (Phase 1: tracked, not enforced). null/blank = uncapped.
        dailyRegularHours: numOrNull(tierForm.dailyRegularHours, parseFloat),
        dailyPrimeHours: numOrNull(tierForm.dailyPrimeHours, parseFloat),
        dailyWeekendHours: numOrNull(tierForm.dailyWeekendHours, parseFloat),
        primeBlocked: !!tierForm.primeBlocked,
        courtReservationsBlocked: !!tierForm.courtReservationsBlocked,
        streamingIncluded: !!tierForm.streamingIncluded,
        // null = inherit settings/membershipTiers.overageBillingEnabled
        overageBillingEnabled: tierForm.overageBillingEnabled == null ? null : !!tierForm.overageBillingEnabled,
        maxCourtsPerDay: numOrNull(tierForm.maxCourtsPerDay, (v) => parseInt(v, 10)),
        maxAdvanceDays: numOrNull(tierForm.maxAdvanceDays, (v) => parseInt(v, 10)),
        // Enforced, unlike the Phase 1 caps: checkGuardrails uses it over the
        // club's maxBookingDurationHours. null = the club's.
        maxBookingDurationHours: numOrNull(tierForm.maxBookingDurationHours, parseFloat),
        // Policy overrides: null = use Court Rentals → Policy defaults, 0 = explicitly zero
        maxHoursPerWeek: numOrNull(tierForm.maxHoursPerWeek, parseFloat),
        maxCourtsPerRequest: numOrNull(tierForm.maxCourtsPerRequest, (v) => parseInt(v, 10)),
        // Guest allowance: null = use default (none), 0 = explicitly zero
        guestsPerMonth: numOrNull(tierForm.guestsPerMonth, (v) => parseInt(v, 10)),
        guestsPerBooking: numOrNull(tierForm.guestsPerBooking, (v) => parseInt(v, 10)),
        enabled: !!tierForm.enabled,
        adminOnly: !!tierForm.adminOnly,
        sortOrder: parseInt(tierForm.sortOrder, 10) || 0,
        // Display fields — saved as-is; empty strings/arrays cause AdultMembership to fall back to hardcoded defaults
        tagline: (tierForm.tagline || '').trim(),
        ctaLabel: (tierForm.ctaLabel || '').trim(),
        priceSuffix: (tierForm.priceSuffix || '').trim(),
        priceFootnote: (tierForm.priceFootnote || '').trim(),
        bullets: (tierForm.bullets || '')
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean),
        emphasize: !!tierForm.emphasize,
        accentColor: (tierForm.accentColor || '').trim(),
      };
      let syncNote = '';
      let syncFailed = false;
      if (editingTierId) {
        await updateDoc(doc(db, 'membershipTiers', editingTierId), data);
        setTiers(prev => prev.map(t => t.id === editingTierId ? { id: editingTierId, ...data } : t)
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)));
        // Caught here rather than left to the outer handler: the tier itself is
        // already saved by this point, and reporting "Failed to save tier"
        // would send the admin back to re-save something that is correct, and
        // to doubt the edit they just made.
        try {
          const n = await syncMemberSnapshots(editingTierId, data);
          if (n) syncNote = ` · ${n} member${n === 1 ? '' : 's'} updated`;
        } catch (e) {
          logError('MembershipTiers', `Member snapshot sync failed: ${e.message}`, e.stack);
          syncFailed = true;
        }
      } else {
        // A brand-new tier has no members to sync yet.
        const ref = await addDoc(collection(db, 'membershipTiers'), { ...data, createdAt: serverTimestamp() });
        setTiers(prev => [...prev, { id: ref.id, ...data }]
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)));
      }
      setTierDialog(false);
      if (syncFailed) {
        setError('Tier saved, but the members on it still show its old weekly hours. '
          + 'Save the tier again to retry — their bookings price from the tier either way.');
      } else {
        setSuccess(`Tier saved${syncNote}.`);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (e) {
      logError('MembershipTiers', e.message, e.stack);
      setError('Failed to save tier.');
    }
    setSaving(false);
  };

  const deleteTier = async (t) => {
    if (!window.confirm(`Delete tier "${t.name}"? Existing members on this tier will not be auto-migrated.`)) return;
    try {
      await deleteDoc(doc(db, 'membershipTiers', t.id));
      setTiers(prev => prev.filter(x => x.id !== t.id));
      setSuccess('Tier deleted.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      logError('MembershipTiers', e.message, e.stack);
      setError('Failed to delete.');
    }
  };

  const saveTierSettings = async (patch) => {
    const next = { ...tierSettings, ...patch };
    setTierSettings(next);
    try {
      await setDoc(doc(db, 'settings', 'membershipTiers'), next, { merge: true });
      setSuccess('Settings saved.');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      logError('MembershipTiers', e.message, e.stack);
      setError('Failed to save settings.');
    }
  };

  const togglePrimeBlock = async (blockId) => {
    // Prime designation lives on the time block doc itself (isPrime flag)
    const tb = timeBlocks.find(t => t.id === blockId);
    const next = !(tb && tb.isPrime === true);
    try {
      await updateDoc(doc(db, 'rentalTimeBlocks', blockId), { isPrime: next });
      setTimeBlocks(prev => prev.map(t => t.id === blockId ? { ...t, isPrime: next } : t));
      setSuccess('Settings saved.');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      logError('MembershipTiers', e.message, e.stack);
      setError('Failed to save prime time setting.');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: branding.chromeBg }}>
              Membership Tiers
            </Typography>
            <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>
              Define member tiers, pricing, and weekly hour allocations.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNewTier} sx={{ backgroundColor: branding.chromeBg }}>
            New Tier
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Settings */}
        <Paper elevation={1} sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #eee', backgroundColor: '#f9fafb' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: branding.chromeBg, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CardMembershipIcon sx={{ fontSize: 20 }} /> Membership Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControlLabel
                  control={<Switch checked={!!tierSettings.policyEnforcementEnabled}
                    onChange={e => saveTierSettings({ policyEnforcementEnabled: e.target.checked })} />}
                  label={<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Enforce reservation policies</Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>OFF: violations are recorded on bookings but nothing is blocked</Typography></Box>} />
                <FormControlLabel
                  control={<Switch checked={!!tierSettings.overageBillingEnabled}
                    onChange={e => saveTierSettings({ overageBillingEnabled: e.target.checked })} />}
                  label={<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Bill court time</Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>OFF: overages accrue in the ledger unbilled, members see no overage messaging</Typography></Box>} />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth type="date" label="Court reservations open"
                value={openDateInput}
                onChange={e => setOpenDateInput(e.target.value)}
                onBlur={() => {
                  if (openDateInput !== (tierSettings.reservationsOpenDate || '')) {
                    saveTierSettings({ reservationsOpenDate: openDateInput });
                  }
                }}
                InputLabelProps={{ shrink: true }}
                helperText="Members cannot reserve courts before this date. Leave blank to open reservations now. Practices, classes and court rentals are unaffected."
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth select label="Billing Model"
                value={tierSettings.billingModel}
                onChange={e => saveTierSettings({ billingModel: e.target.value })}
                helperText="When members are charged for overage hours"
                sx={{ backgroundColor: 'white', borderRadius: 1 }}
              >
                {BILLING_MODELS.map(bm => <MenuItem key={bm.value} value={bm.value}>{bm.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth type="number" label="Payment grace period"
                value={tierSettings.paymentGraceDays ?? 0}
                onChange={e => saveTierSettings({ paymentGraceDays: Math.max(0, Number(e.target.value) || 0) })}
                inputProps={{ min: 0, step: 1 }}
                helperText="Days after an invoice before unpaid court time holds future bookings. 0 = straight away."
                sx={{ backgroundColor: 'white', borderRadius: 1 }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth select label="Required Waiver"
                value={tierSettings.requiredWaiverId || ''}
                onChange={e => saveTierSettings({ requiredWaiverId: e.target.value })}
                helperText={
                  waiverDocs.length === 0
                    ? 'No eligible documents yet. Create an "accept" or "signature" document in Admin → Documents first.'
                    : 'Members must have a current acceptance of this document before purchase. Configured in Admin → Documents.'
                }
                sx={{ backgroundColor: 'white', borderRadius: 1 }}
              >
                <MenuItem value="">— No waiver required —</MenuItem>
                {waiverDocs.map(d => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                    {d.expirationDays > 0 ? ` (${d.expirationDays}-day expiration)` : ' (never expires)'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#555', mb: 1 }}>
                Prime Time Blocks — bookings overlapping these blocks deduct from member prime hours
              </Typography>
              {timeBlocks.length === 0 ? (
                <Typography variant="caption" sx={{ color: '#888' }}>
                  No time blocks defined. Create them in Court Rentals → Time Blocks tab first.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {timeBlocks.map(tb => {
                    const sel = tb.isPrime === true;
                    return (
                      <Box
                        key={tb.id}
                        onClick={() => togglePrimeBlock(tb.id)}
                        sx={{
                          px: 1.5, py: 0.6, borderRadius: 1, cursor: 'pointer',
                          fontSize: '0.82rem', fontWeight: 600,
                          border: `2px solid ${sel ? branding.chromeBg : '#ddd'}`,
                          backgroundColor: sel ? branding.chromeBg : 'white',
                          color: sel ? 'white' : '#555',
                          '&:hover': { borderColor: branding.chromeBg },
                        }}
                      >
                        {tb.name}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Tier cards */}
        <Grid container spacing={2}>
          {tiers.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Typography sx={{ color: '#888', textAlign: 'center', py: 4 }}>
                No membership tiers defined yet. Add your first tier.
              </Typography>
            </Grid>
          )}
          {tiers.map(t => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={t.id}>
              <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #eee', opacity: t.enabled === false ? 0.55 : 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: branding.chromeBg }}>{t.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3, flexWrap: 'wrap' }}>
                      {t.enabled === false && <Chip label="Disabled" size="small" sx={{ fontSize: '0.65rem', height: 18 }} />}
                      {t.adminOnly && <Chip label="Admin-Only" size="small" color="warning" sx={{ fontSize: '0.65rem', height: 18 }} />}
                    </Box>
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => openEditTier(t)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => deleteTier(t)}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#27AE60', mb: 1 }}>
                  ${(t.monthlyPrice || 0).toFixed(2)}
                  <Typography component="span" variant="caption" sx={{ color: '#888', ml: 0.5 }}>
                    {t.pricingMode === 'court_rental' ? 'court rental' : t.pricingMode === 'one_time' ? 'one-time' : '/ month'}
                  </Typography>
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ color: '#555' }}>
                    <strong>{t.weeklyRegularHours || 0}</strong> regular hrs / week
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#555' }}>
                    <strong>{t.weeklyPrimeHours || 0}</strong> prime hrs / week
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#555' }}>
                    <strong>{t.weeklyWeekendHours || 0}</strong> weekend hrs / week
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    {t.courtReservationsBlocked ? 'No court reservations' : `Daily caps: ${t.dailyRegularHours ?? '—'} regular · ${t.primeBlocked ? 'blocked' : (t.dailyPrimeHours ?? '—')} prime · ${t.dailyWeekendHours ?? '—'} weekend`}
                    {t.streamingIncluded && ' · court camera'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    {(t.maxCourtsPerDay ?? '—')} court{t.maxCourtsPerDay === 1 ? '' : 's'}/day · {t.maxAdvanceDays ? `${t.maxAdvanceDays}-day booking window` : 'no window limit'}
                    {' · '}{t.maxBookingDurationHours == null
                      ? 'longest reservation: club setting'
                      : t.maxBookingDurationHours === 0 ? 'no reservation length limit'
                        : `up to ${t.maxBookingDurationHours} h per reservation`}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888', mt: 0.5 }}>
                    {t.pricingMode === 'court_rental' ? 'Hourly' : 'Overage'}: ${(t.overageRegularRate || 0).toFixed(2)}/hr regular · {t.primeBlocked ? 'prime blocked' : `$${(t.overagePrimeRate || 0).toFixed(2)}/hr prime`} · ${(t.overageWeekendRate || 0).toFixed(2)}/hr weekend
                  </Typography>
                  {(t.guestsPerMonth != null || t.guestsPerBooking != null) && (
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      Guests: {t.guestsPerMonth ?? '—'}/month · {t.guestsPerBooking ?? '—'}/booking
                    </Typography>
                  )}
                </Box>
                {t.stripePriceId && (
                  <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mt: 1, fontFamily: 'monospace', fontSize: '0.65rem' }}>
                    {t.stripePriceId}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Tier Edit Dialog */}
        <Dialog open={tierDialog} onClose={() => setTierDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: branding.chromeBg, color: 'white' }}>
            {editingTierId ? 'Edit Membership Tier' : 'New Membership Tier'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {tiers.filter(t => t.id !== editingTierId).length > 0 && (
                <>
                  <TextField select fullWidth size="small" label="Copy settings from another plan"
                    value={copyFromId} onChange={e => copySettingsFrom(e.target.value)}
                    helperText="Fills hours, caps, limits and overage rates. Name, price and Stripe price ID are never copied.">
                    <MenuItem value="">— None —</MenuItem>
                    {tiers.filter(t => t.id !== editingTierId).map(t => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name || t.id}{t.adminOnly ? ' (admin-only)' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                  {copyNote && (
                    <Alert severity="info" onClose={() => setCopyNote('')} sx={{ mt: -1 }}>{copyNote}</Alert>
                  )}
                  <Divider />
                </>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth label="Tier Name *" value={tierForm.name}
                  onChange={e => setTierForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Silver"
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  fullWidth label="URL Slug (optional)" value={tierForm.slug}
                  onChange={e => setTierForm(f => ({ ...f, slug: e.target.value }))}
                  placeholder="e.g. adult-training"
                  helperText="Shareable link: /m/<slug> — leave blank to auto-generate from the tier name"
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  label="Sort" type="number" value={tierForm.sortOrder}
                  onChange={e => setTierForm(f => ({ ...f, sortOrder: e.target.value }))}
                  inputProps={{ min: 0 }}
                  sx={{ width: 90, backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
              </Box>

              {(slugify(tierForm.slug) || slugify(tierForm.name)) && (
                <Alert severity="info" sx={{ wordBreak: 'break-all', '& .MuiAlert-message': { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, width: '100%' } }}>
                  <span>
                    Shareable link{!slugify(tierForm.slug) ? ' (auto from name)' : ''}:{' '}
                    <strong>{`${window.location.origin}/m/${slugify(tierForm.slug) || slugify(tierForm.name)}`}</strong>
                  </span>
                  <IconButton size="small" title="Copy link"
                    onClick={() => {
                      const url = `${window.location.origin}/m/${slugify(tierForm.slug) || slugify(tierForm.name)}`;
                      navigator.clipboard.writeText(url).then(
                        () => { setSuccess('Link copied to clipboard'); setTimeout(() => setSuccess(''), 3000); },
                        () => setError('Could not copy — long-press the link to copy manually')
                      );
                    }}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Alert>
              )}
              <TextField
                fullWidth select label="Pricing Mode *" value={tierForm.pricingMode}
                onChange={e => setTierForm(f => ({ ...f, pricingMode: e.target.value, isDayPass: e.target.value === 'one_time' ? f.isDayPass : false }))}
                helperText={
                  tierForm.pricingMode === 'court_rental'
                    ? 'No subscription and no Stripe. Assigned by an admin; court time is invoiced from the overage ledger.'
                    : tierForm.pricingMode === 'recurring'
                      ? 'Subscription billed monthly via Stripe'
                      : 'One-time charge (e.g. day pass, annual)'
                }
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
              >
                <MenuItem value="recurring">Recurring (monthly subscription)</MenuItem>
                <MenuItem value="one_time">One-time (day pass, single charge)</MenuItem>
                <MenuItem value="court_rental">Court rental only (no charge, invoiced per hour)</MenuItem>
              </TextField>

              {tierForm.pricingMode === 'one_time' && (
                <FormControlLabel
                  control={<Switch checked={!!tierForm.isDayPass} onChange={e => setTierForm(f => ({ ...f, isDayPass: e.target.checked }))} />}
                  label={tierForm.isDayPass
                    ? 'Day Pass — expires at end of day, uses mini-registration (no account required)'
                    : 'Full registration'}
                />
              )}

              <TextField
                fullWidth
                label={tierForm.pricingMode === 'court_rental' ? 'Price (leave 0)' : tierForm.pricingMode === 'recurring' ? 'Monthly Price *' : 'Price *'}
                type="number" value={tierForm.monthlyPrice}
                onChange={e => setTierForm(f => ({ ...f, monthlyPrice: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
              />

              {tierForm.pricingMode === 'recurring' && (
                <TextField
                  fullWidth
                  label={tierForm.adminOnly ? 'Stripe Price ID (optional)' : 'Stripe Price ID *'}
                  value={tierForm.stripePriceId}
                  onChange={e => setTierForm(f => ({ ...f, stripePriceId: e.target.value }))}
                  placeholder="price_..."
                  helperText={tierForm.adminOnly
                    ? 'Optional for admin-only tiers (leave blank for comped memberships)'
                    : 'Recurring price ID from Stripe (live mode)'}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
              )}

              <Divider><Typography variant="caption" sx={{ color: '#888' }}>Weekly Hour Allocation</Typography></Divider>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth label="Regular Hours / week" type="number"
                  value={tierForm.weeklyRegularHours}
                  onChange={e => setTierForm(f => ({ ...f, weeklyRegularHours: e.target.value }))}
                  inputProps={{ min: 0, step: 0.5 }}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  fullWidth label="Prime Hours / week" type="number"
                  value={tierForm.weeklyPrimeHours}
                  onChange={e => setTierForm(f => ({ ...f, weeklyPrimeHours: e.target.value }))}
                  inputProps={{ min: 0, step: 0.5 }}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  fullWidth label="Weekend Hours / week" type="number"
                  value={tierForm.weeklyWeekendHours}
                  onChange={e => setTierForm(f => ({ ...f, weeklyWeekendHours: e.target.value }))}
                  inputProps={{ min: 0, step: 0.5 }}
                  helperText="Sat/Sun bookings — no prime split on weekends"
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
              </Box>

              {/* On a court-rental tier these are not overage at all: the tier
                  includes no hours, so every hour booked is charged at these
                  rates. Calling them overage cost real confusion — a renter's
                  $20/hr prime read as a penalty on an allowance he never had.
                  Same fields, named for what they do on this tier. */}
              <Divider><Typography variant="caption" sx={{ color: '#888' }}>
                {isCourtRentalTier
                  ? 'Hourly Rates (this tier includes no hours — every hour is charged)'
                  : 'Overage Rates (when allocation runs out)'}
              </Typography></Divider>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth label={isCourtRentalTier ? 'Regular / hr' : 'Regular Overage / hr'} type="number"
                  value={tierForm.overageRegularRate}
                  onChange={e => setTierForm(f => ({ ...f, overageRegularRate: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  fullWidth label={isCourtRentalTier ? 'Prime / hr' : 'Prime Overage / hr'} type="number"
                  value={tierForm.overagePrimeRate}
                  onChange={e => setTierForm(f => ({ ...f, overagePrimeRate: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  fullWidth label={isCourtRentalTier ? 'Weekend / hr' : 'Weekend Overage / hr'} type="number"
                  value={tierForm.overageWeekendRate}
                  onChange={e => setTierForm(f => ({ ...f, overageWeekendRate: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
              </Box>

              <TextField
                label="Longest reservation (hours)" type="number"
                value={tierForm.maxBookingDurationHours}
                onChange={e => setTierForm(f => ({ ...f, maxBookingDurationHours: e.target.value }))}
                inputProps={{ min: 0, step: 0.5 }}
                helperText="Enforced. Blank = the club's Reservation Guardrails (Court Rentals → Policy). 0 = no limit."
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1, maxWidth: 360 }}
              />

              <Divider><Typography variant="caption" sx={{ color: '#888' }}>Daily Caps &amp; Booking Policy (Phase 1: tracked, not enforced)</Typography></Divider>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth label="Regular Hours / day" type="number"
                  value={tierForm.dailyRegularHours}
                  onChange={e => setTierForm(f => ({ ...f, dailyRegularHours: e.target.value }))}
                  inputProps={{ min: 0, step: 0.5 }} helperText="Blank = no daily cap"
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  fullWidth label="Prime Hours / day" type="number"
                  value={tierForm.dailyPrimeHours}
                  onChange={e => setTierForm(f => ({ ...f, dailyPrimeHours: e.target.value }))}
                  inputProps={{ min: 0, step: 0.5 }} helperText="Blank = no daily cap"
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  fullWidth label="Weekend Hours / day" type="number"
                  value={tierForm.dailyWeekendHours}
                  onChange={e => setTierForm(f => ({ ...f, dailyWeekendHours: e.target.value }))}
                  inputProps={{ min: 0, step: 0.5 }} helperText="Blank = no daily cap"
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  select fullWidth label="Overage billing"
                  value={tierForm.overageBillingEnabled === null ? 'inherit' : (tierForm.overageBillingEnabled ? 'on' : 'off')}
                  onChange={e => setTierForm(f => ({
                    ...f,
                    overageBillingEnabled: e.target.value === 'inherit' ? null : e.target.value === 'on',
                  }))}
                  helperText="Whether members on this tier are shown and charged for hours beyond their allowance"
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                >
                  <MenuItem value="inherit">Use club setting</MenuItem>
                  <MenuItem value="on">On for this tier</MenuItem>
                  <MenuItem value="off">Off for this tier</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  fullWidth label="Courts / day" type="number"
                  value={tierForm.maxCourtsPerDay}
                  onChange={e => setTierForm(f => ({ ...f, maxCourtsPerDay: e.target.value }))}
                  inputProps={{ min: 1, step: 1 }} helperText="Distinct courts bookable per day"
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  fullWidth label="Booking Window (days)" type="number"
                  value={tierForm.maxAdvanceDays}
                  onChange={e => setTierForm(f => ({ ...f, maxAdvanceDays: e.target.value }))}
                  inputProps={{ min: 1, step: 1 }} helperText="e.g. 14 = 2 weeks, 28 = 4 weeks"
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <FormControlLabel sx={{ minWidth: 170 }}
                  control={<Switch checked={tierForm.primeBlocked}
                    onChange={e => setTierForm(f => ({ ...f, primeBlocked: e.target.checked }))} />}
                  label="Prime time blocked" />
                <FormControlLabel
                  control={<Switch checked={tierForm.courtReservationsBlocked}
                    onChange={e => setTierForm(f => ({ ...f, courtReservationsBlocked: e.target.checked }))} />}
                  label="Cannot reserve courts" />
                {/* Reads the opposite way to the two switches beside it, and
                    means it: those withhold court time a membership would
                    otherwise include, this one grants a perk no tier has until
                    someone says so. A new tier starts with no camera. */}
                <FormControlLabel
                  control={<Switch checked={tierForm.streamingIncluded}
                    onChange={e => setTierForm(f => ({ ...f, streamingIncluded: e.target.checked }))} />}
                  label="Court camera included" />
              </Box>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: -1, mb: 1 }}>
                "Cannot reserve courts" blocks this tier from the member reservation system entirely
                (e.g. Day Pass, Training Day). "Prime time blocked" allows regular and weekend
                bookings but no prime hours.
              </Typography>

              <Divider>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  Reservation Policy Overrides (leave blank to use Court Rentals → Policy defaults)
                </Typography>
              </Divider>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth label="Max Hours / week" type="number"
                  value={tierForm.maxHoursPerWeek}
                  onChange={e => setTierForm(f => ({ ...f, maxHoursPerWeek: e.target.value }))}
                  helperText="Cap on total booking hours per week. Blank = no cap from tier."
                  inputProps={{ min: 0, step: 0.5 }}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  fullWidth label="Max Courts / request" type="number"
                  value={tierForm.maxCourtsPerRequest}
                  onChange={e => setTierForm(f => ({ ...f, maxCourtsPerRequest: e.target.value }))}
                  helperText="Blank = use global Default Max Courts."
                  inputProps={{ min: 0, max: 6, step: 1 }}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
              </Box>

              <Divider>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  Guest Allowance (gate access codes — coming soon)
                </Typography>
              </Divider>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth label="Guests / month" type="number"
                  value={tierForm.guestsPerMonth}
                  onChange={e => setTierForm(f => ({ ...f, guestsPerMonth: e.target.value }))}
                  helperText="Total guests per calendar month. Blank = none. 0 = explicitly none."
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  fullWidth label="Guests / booking" type="number"
                  value={tierForm.guestsPerBooking}
                  onChange={e => setTierForm(f => ({ ...f, guestsPerBooking: e.target.value }))}
                  helperText="Max guests on a single reservation. Blank = none. 0 = explicitly none."
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
              </Box>

              <Divider><Typography variant="caption" sx={{ color: '#888' }}>Public Page Display</Typography></Divider>

              <TextField
                fullWidth label="Tagline" value={tierForm.tagline}
                onChange={e => setTierForm(f => ({ ...f, tagline: e.target.value }))}
                placeholder='e.g. "Most popular" or "One-time access"'
                helperText="Shown above the price on the public page. Blank = use default."
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
              />

              <TextField
                fullWidth multiline minRows={3} maxRows={8}
                label="Feature Bullets" value={tierForm.bullets}
                onChange={e => setTierForm(f => ({ ...f, bullets: e.target.value }))}
                placeholder={"e.g.\nReserve court time each week\n1 guest pass per month\nMember-only events\nCancel anytime"}
                helperText="One bullet per line. Blank = use default."
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth label="CTA Button Label" value={tierForm.ctaLabel}
                  onChange={e => setTierForm(f => ({ ...f, ctaLabel: e.target.value }))}
                  placeholder='e.g. "Join Now →"'
                  helperText="Blank = use default."
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  fullWidth label="Accent Color" value={tierForm.accentColor}
                  onChange={e => setTierForm(f => ({ ...f, accentColor: e.target.value }))}
                  placeholder="#C9A84C"
                  helperText="Hex color for the colored dot. Blank = default gold."
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth label="Price Suffix" value={tierForm.priceSuffix}
                  onChange={e => setTierForm(f => ({ ...f, priceSuffix: e.target.value }))}
                  placeholder='e.g. "/month" or "/year"'
                  helperText="Shown next to price. Blank = auto (/month for recurring)."
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  fullWidth label="Price Footnote" value={tierForm.priceFootnote}
                  onChange={e => setTierForm(f => ({ ...f, priceFootnote: e.target.value }))}
                  placeholder='e.g. "Billed monthly · Cancel anytime"'
                  helperText="Small text below price. Blank = use default."
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
              </Box>

              <FormControlLabel
                control={<Switch checked={!!tierForm.emphasize} onChange={e => setTierForm(f => ({ ...f, emphasize: e.target.checked }))} />}
                label={tierForm.emphasize
                  ? 'Featured — this tier is visually emphasized (raised card, badge)'
                  : 'Standard — normal display'}
              />

              <FormControlLabel
                control={<Switch checked={tierForm.enabled} onChange={e => setTierForm(f => ({ ...f, enabled: e.target.checked }))} />}
                label={tierForm.enabled ? 'Enabled — tier is purchasable' : 'Disabled — hidden from purchase pages'}
              />
              <FormControlLabel
                control={<Switch checked={!!tierForm.adminOnly} onChange={e => setTierForm(f => ({ ...f, adminOnly: e.target.checked }))} />}
                label={tierForm.adminOnly
                  ? 'Admin-only — hidden from public pages, assigned manually (Stripe optional)'
                  : 'Public — visible on membership purchase pages'}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setTierDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveTier} disabled={saving} sx={{ backgroundColor: branding.chromeBg }}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
